import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Users, CheckCircle, XCircle, AlertCircle, Flag, UserCheck, RefreshCw, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function GuestsTab() {
  const [registeredGuests, setRegisteredGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);

  useEffect(() => {
    fetchRegisteredGuests();
  }, []);

  const fetchRegisteredGuests = async () => {
    setLoadingGuests(true);
    try {
      const response = await axios.get(`${API}/admin/guests`);
      setRegisteredGuests(response.data);
    } catch (error) {
      console.error("Failed to load registered guests");
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleVerifyGuest = async (employeeNumber) => {
    try {
      await axios.post(`${API}/admin/guests/${employeeNumber}/verify`);
      toast.success("Guest verified!");
      fetchRegisteredGuests();
    } catch (error) {
      toast.error("Failed to verify guest");
    }
  };

  const handleBulkVerifyAll = async () => {
    const pendingGuests = registeredGuests.filter(g => !g.is_verified);
    if (pendingGuests.length === 0) {
      toast.info("No pending guests to verify");
      return;
    }
    try {
      const employeeNumbers = pendingGuests.map(g => g.employee_number);
      await axios.post(`${API}/admin/guests/bulk-verify`, { employee_numbers: employeeNumbers });
      toast.success(`Verified ${pendingGuests.length} guests!`);
      fetchRegisteredGuests();
    } catch (error) {
      toast.error("Failed to bulk verify guests");
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Guest Verification</h1>
          <p className="text-vault-text-secondary font-manrope mt-1">Review and verify registered guests</p>
        </div>
        <div className="flex gap-2">
          {registeredGuests.filter(g => !g.is_verified).length > 0 && (
            <Button
              onClick={handleBulkVerifyAll}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-verify-all-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Verify All Pending ({registeredGuests.filter(g => !g.is_verified).length})
            </Button>
          )}
          <Button
            onClick={fetchRegisteredGuests}
            className="bg-vault-surface border border-vault-border hover:bg-vault-surface-highlight text-vault-text"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-vault-gold/20 rounded-lg"><Users className="w-5 h-5 text-vault-gold" /></div>
              <div>
                <p className="text-vault-text-secondary text-sm">Total Guests</p>
                <p className="text-2xl font-bold text-vault-text font-mono">{registeredGuests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg"><CheckCircle className="w-5 h-5 text-green-500" /></div>
              <div>
                <p className="text-vault-text-secondary text-sm">Verified</p>
                <p className="text-2xl font-bold text-green-500 font-mono">{registeredGuests.filter(g => g.is_verified).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg"><AlertCircle className="w-5 h-5 text-amber-500" /></div>
              <div>
                <p className="text-vault-text-secondary text-sm">Pending</p>
                <p className="text-2xl font-bold text-amber-500 font-mono">{registeredGuests.filter(g => !g.is_verified && !g.is_blocked).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg"><XCircle className="w-5 h-5 text-red-500" /></div>
              <div>
                <p className="text-vault-text-secondary text-sm">Blocked</p>
                <p className="text-2xl font-bold text-red-500 font-mono">{registeredGuests.filter(g => g.is_blocked).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-vault-surface border-vault-border">
        <CardHeader className="border-b border-vault-border">
          <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-vault-gold" />
            Registered Guests
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow className="table-header border-vault-border hover:bg-transparent">
                  <TableHead className="text-vault-gold">Status</TableHead>
                  <TableHead className="text-vault-gold">Name</TableHead>
                  <TableHead className="text-vault-gold">Employee ID</TableHead>
                  <TableHead className="text-vault-gold">Check-ins</TableHead>
                  <TableHead className="text-vault-gold">Registered</TableHead>
                  <TableHead className="text-vault-gold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingGuests ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-vault-text-secondary py-8">Loading guests...</TableCell>
                  </TableRow>
                ) : registeredGuests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-vault-text-secondary py-8">No registered guests yet.</TableCell>
                  </TableRow>
                ) : (
                  registeredGuests.map((guest) => (
                    <TableRow key={guest.id} className={`table-row border-vault-border ${guest.is_blocked ? 'bg-red-900/30' : guest.is_flagged ? 'bg-red-900/20' : !guest.is_verified ? 'bg-amber-900/20' : ''}`}>
                      <TableCell>
                        {guest.is_blocked ? (
                          <span className="flex items-center gap-1 text-red-500"><XCircle className="w-4 h-4" />Blocked</span>
                        ) : guest.is_flagged ? (
                          <span className="flex items-center gap-1 text-red-400"><Flag className="w-4 h-4" />Flagged</span>
                        ) : guest.is_verified ? (
                          <span className="flex items-center gap-1 text-green-400"><CheckCircle className="w-4 h-4" />Verified</span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-400"><AlertCircle className="w-4 h-4" />Pending</span>
                        )}
                      </TableCell>
                      <TableCell className="text-vault-text font-medium">{guest.name}</TableCell>
                      <TableCell className="font-mono text-vault-gold">{guest.employee_number}</TableCell>
                      <TableCell className="text-vault-text">{guest.check_in_count || 0}</TableCell>
                      <TableCell className="text-vault-text-secondary text-sm">{new Date(guest.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {!guest.is_verified && !guest.is_blocked && (
                            <Button variant="ghost" size="sm" className="text-green-400 hover:text-green-300 hover:bg-green-500/20 h-8 px-2" onClick={() => handleVerifyGuest(guest.employee_number)} title="Verify guest">
                              <CheckCircle className="w-4 h-4 mr-1" />Verify
                            </Button>
                          )}
                          {!guest.is_blocked ? (
                            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 px-2"
                              onClick={async () => {
                                if (!confirm(`Block ${guest.name}? They won't be able to check in again.`)) return;
                                try {
                                  await axios.post(`${API}/admin/guests/${guest.employee_number}/block`);
                                  toast.success(`${guest.name} has been blocked`);
                                  fetchRegisteredGuests();
                                } catch (error) { toast.error("Failed to block guest"); }
                              }} title="Block guest">
                              <XCircle className="w-4 h-4 mr-1" />Block
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 h-8 px-2"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/admin/guests/${guest.employee_number}/unblock`);
                                  toast.success(`${guest.name} has been unblocked`);
                                  fetchRegisteredGuests();
                                } catch (error) { toast.error("Failed to unblock guest"); }
                              }} title="Unblock guest">
                              <CheckCircle className="w-4 h-4 mr-1" />Unblock
                            </Button>
                          )}
                          {!guest.is_verified && (
                            <Button variant="ghost" size="sm" className="text-rose-500 hover:text-rose-400 hover:bg-rose-500/20 h-8 px-2"
                              onClick={async () => {
                                if (!confirm(`Remove ${guest.name} from system? This cannot be undone.`)) return;
                                try {
                                  await axios.delete(`${API}/admin/guests/${guest.employee_number}`);
                                  toast.success(`${guest.name} removed from system`);
                                  fetchRegisteredGuests();
                                } catch (error) { toast.error("Failed to remove guest"); }
                              }} title="Remove guest">
                              <Trash2 className="w-4 h-4 mr-1" />Remove
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </>
  );
}
